import { useForm, useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useMemo, useState } from "react";

import { ContentStatusBadge } from "~/components/admin/content-status-badge";
import { DraftReviewCard } from "~/components/admin/draft-review-card";
import { FaqEditor } from "~/components/admin/faq-editor";
import { ImageUploadField } from "~/components/admin/image-upload-field";
import { PageChangeHistory } from "~/components/admin/page-change-history";
import { ReverseLookupCard } from "~/components/admin/reverse-lookup-card";
import { FrameworkSelection } from "~/components/framework-selection";
import { GroupedMultiSelect } from "~/components/grouped-multi-select";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RichTextEditor } from "~/components/ui/rich-text-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { cihCcosQueryOptions } from "~/lib/functions/cih/ccos";
import {
  approveCtpFn,
  archiveCtpFn,
  createCtpDraftFn,
  ctpQueryKeys,
  ctpQueryOptions,
  getCtpCoachCountFn,
  getCtpAccreditorIdsFn,
  getCtpFrameworkIdsFn,
  promoteCtpDraftFn,
  publishCtpFn,
  restoreCtpFn,
  returnCtpToDraftFn,
  syncCtpFrameworksFn,
  updateCihCtpWithAccreditorsFn,
  type CihCtpRow,
} from "~/lib/functions/cih/ctps";
import { getGroupedFrameworksFn } from "~/lib/functions/cih/frameworks";
import {
  discardDraftAsStaffFn,
  pageChangeQueryKeys,
} from "~/lib/functions/organization-pages";
import { CCOType } from "~/lib/schemas/db-enums";

const CCO_TYPE_DISPLAY_NAME: Record<string, string> = {
  [CCOType.PAO]: "Accreditation bodies",
  [CCOType.CTO]: "Training organizations",
};

export const Route = createFileRoute("/admin/cih/ctps/$ctpId")({
  staticData: { title: "Edit Training Program" },
  loader: async ({ context, params }) => {
    const [
      data,
      linkedFrameworkIds,
      linkedAccreditorIds,
      groupedFrameworks,
      coachCount,
    ] = await Promise.all([
      context.queryClient.ensureQueryData(ctpQueryOptions(params.ctpId)),
      getCtpFrameworkIdsFn({ data: { ctpId: params.ctpId } }),
      getCtpAccreditorIdsFn({ data: { ctpId: params.ctpId } }),
      getGroupedFrameworksFn(),
      getCtpCoachCountFn({ data: { ctpId: params.ctpId } }),
    ]);
    return {
      title: data?.name ?? "Edit Training Program",
      linkedFrameworkIds,
      linkedAccreditorIds,
      groupedFrameworks,
      coachCount,
    };
  },
  component: CtpEditorPage,
});

function CtpEditorPage() {
  const { ctpId } = Route.useParams();
  const {
    linkedFrameworkIds,
    linkedAccreditorIds,
    groupedFrameworks,
    coachCount,
  } = Route.useLoaderData();
  const { data: ctp, isLoading } = useQuery(ctpQueryOptions(ctpId));

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!ctp) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Training program not found.</div>
      </div>
    );
  }

  return (
    <CtpEditorForm
      ctpId={ctpId}
      ctp={ctp}
      initialFrameworkIds={linkedFrameworkIds}
      initialAccreditorIds={linkedAccreditorIds}
      groupedFrameworks={groupedFrameworks}
      coachCount={coachCount}
    />
  );
}

function CtpEditorForm({
  ctpId,
  ctp,
  initialFrameworkIds,
  initialAccreditorIds,
  groupedFrameworks,
  coachCount,
}: {
  ctpId: string;
  ctp: CihCtpRow;
  initialFrameworkIds: string[];
  initialAccreditorIds: string[];
  groupedFrameworks: Record<
    string,
    { id: string; name: string; type: string }[]
  >;
  coachCount: number;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedFrameworkIds, setSelectedFrameworkIds] =
    useState<string[]>(initialFrameworkIds);

  const { data: ccos } = useQuery(cihCcosQueryOptions());

  const form = useForm({
    defaultValues: {
      ccoId: ctp.ccoId ?? "",
      website: ctp.website ?? "",
      logoUrl: ctp.logoUrl ?? "",
      deliveryFormat: ctp.deliveryFormat ?? "",
      accreditationLevel: ctp.accreditationLevel ?? "",
      duration: ctp.duration ?? "",
      tuitionRange: ctp.tuitionRange ?? "",
      prerequisites: ctp.prerequisites ?? "",
      icfHours: ctp.icfHours ?? "",
      pullQuote: ctp.pullQuote,
      pullQuoteAttribution: ctp.pullQuoteAttribution,
      narrativeStats: ctp.narrativeStats,
      hidePlatformStats: ctp.hidePlatformStats,
      description: ctp.description ?? "",
      body: ctp.body != null ? JSON.stringify(ctp.body) : "",
      faqs: (ctp.faqs ?? []) as { question: string; answer: string }[],
      // Submitted with the rest of the form (as `ccoIds`), so it belongs in
      // the form store and not beside it: this is what gives the picker the
      // same dirty tracking and reset behaviour as every other field.
      accreditorIds: initialAccreditorIds,
    },
  });

  // The delivering school cannot also accredit the program. Read the owner from
  // the form's live value rather than the saved row, so repointing Organization
  // at an org currently selected as an accreditor drops that selection on the
  // spot — the picker and the save then agree on what the admin can see. The
  // server validates the same rule against the final state, so this is for
  // clarity in the UI, not the guarantee.
  const liveCcoId = useStore(form.store, (state) => state.values.ccoId);
  const deliveringCcoId = liveCcoId || ctp.ccoId;

  const selectedAccreditorIds = useStore(
    form.store,
    (state) => state.values.accreditorIds,
  );
  const accreditorIds = useMemo(
    () => selectedAccreditorIds.filter((id) => id !== deliveringCcoId),
    [selectedAccreditorIds, deliveringCcoId],
  );

  // Accreditation bodies first — they are what this field is normally for.
  const groupedAccreditors = useMemo(() => {
    const selectable = (ccos ?? []).filter((cco) => cco.id !== deliveringCcoId);
    return {
      [CCOType.PAO]: selectable.filter((cco) => cco.type === CCOType.PAO),
      [CCOType.CTO]: selectable.filter((cco) => cco.type === CCOType.CTO),
    };
  }, [ccos, deliveringCcoId]);

  const showSuccess = (msg: string): void => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ctpQueryKeys.all });
    queryClient.invalidateQueries({
      queryKey: ctpQueryKeys.detail(ctpId),
    });
    queryClient.invalidateQueries({ queryKey: pageChangeQueryKeys.all });
  };

  /** After the draft is published or discarded, open the live row. */
  const openPublished = ({ publishedId }: { publishedId: string }): void => {
    queryClient.removeQueries({ queryKey: ctpQueryKeys.detail(ctpId) });
    invalidate();
    navigate({ to: "/admin/cih/ctps/$ctpId", params: { ctpId: publishedId } });
  };

  const getFormData = () => {
    const v = form.state.values;
    return {
      id: ctpId,
      ccoId: v.ccoId || null,
      website: v.website,
      logoUrl: v.logoUrl,
      deliveryFormat: v.deliveryFormat,
      accreditationLevel: v.accreditationLevel,
      duration: v.duration,
      tuitionRange: v.tuitionRange,
      prerequisites: v.prerequisites,
      icfHours: v.icfHours,
      pullQuote: v.pullQuote,
      pullQuoteAttribution: v.pullQuoteAttribution,
      narrativeStats: v.narrativeStats,
      hidePlatformStats: v.hidePlatformStats,
      description: v.description,
      body: v.body,
      faqs: v.faqs,
    };
  };

  const saveAll = async () => {
    // Owner and accreditors go together: either ordering of two separate
    // writes rejects a legitimate swap (moving the program to a body that
    // currently accredits it, while recording the old school as accreditor),
    // and a failure between them would half-apply the save.
    await updateCihCtpWithAccreditorsFn({
      data: { update: getFormData(), ccoIds: accreditorIds },
    });
    await syncCtpFrameworksFn({
      data: { ctpId, frameworkIds: selectedFrameworkIds },
    });
  };

  const updateMutation = useMutation({
    mutationFn: saveAll,
    onSuccess: () => {
      invalidate();
      showSuccess("Training program saved.");
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (data: { data: { id: string } }) => {
      await saveAll();
      return publishCtpFn(data);
    },
    onSuccess: () => {
      invalidate();
      showSuccess("Training program published.");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: archiveCtpFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Training program archived.");
    },
  });

  const createDraftMutation = useMutation({
    mutationFn: createCtpDraftFn,
    onSuccess: (newDraft) => {
      invalidate();
      navigate({
        to: "/admin/cih/ctps/$ctpId",
        params: { ctpId: newDraft.id },
      });
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async (data: { data: { draftId: string } }) => {
      await saveAll();
      return promoteCtpDraftFn(data);
    },
    onSuccess: openPublished,
  });

  const discardDraftMutation = useMutation({
    mutationFn: discardDraftAsStaffFn,
    onSuccess: openPublished,
  });

  const approveMutation = useMutation({
    mutationFn: approveCtpFn,
    onSuccess: (result) => {
      if (result.publishedId !== ctpId) {
        openPublished(result);
        return;
      }
      invalidate();
      showSuccess("Training program approved.");
    },
  });

  const returnToDraftMutation = useMutation({
    mutationFn: returnCtpToDraftFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Returned to draft.");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: restoreCtpFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Training program restored to draft.");
    },
  });

  const handleSave = (): void => {
    updateMutation.mutate();
  };

  const anyPending =
    updateMutation.isPending ||
    publishMutation.isPending ||
    archiveMutation.isPending ||
    createDraftMutation.isPending ||
    promoteMutation.isPending ||
    approveMutation.isPending ||
    returnToDraftMutation.isPending ||
    restoreMutation.isPending ||
    discardDraftMutation.isPending;

  const mutationError =
    updateMutation.error ??
    publishMutation.error ??
    archiveMutation.error ??
    createDraftMutation.error ??
    promoteMutation.error ??
    approveMutation.error ??
    returnToDraftMutation.error ??
    restoreMutation.error ??
    discardDraftMutation.error;

  const isDraft = ctp.status === "draft";
  const isPublished = ctp.status === "published";
  const isInReview = ctp.status === "in_review";
  const isArchived = ctp.status === "archived";
  const hasDraftParent = ctp.draftOf != null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-medium text-gray-900">{ctp.name}</h1>
              <ContentStatusBadge status={ctp.status} />
            </div>
            <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
              <span>Training program editor — ID: {ctpId}</span>
              {ctp.status === "published" && ctp.slug && (
                <a
                  href={`/training-programs/${ctp.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-600 hover:underline"
                >
                  View public page
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {isDraft && !hasDraftParent && (
              <>
                <Button
                  variant="default"
                  onClick={handleSave}
                  disabled={anyPending}
                  loading={updateMutation.isPending}
                >
                  Save
                </Button>
                <Button
                  variant="primary"
                  onClick={() =>
                    publishMutation.mutate({ data: { id: ctpId } })
                  }
                  disabled={anyPending}
                  loading={publishMutation.isPending}
                >
                  Publish
                </Button>
              </>
            )}
            {isDraft && hasDraftParent && (
              <Button
                variant="default"
                onClick={handleSave}
                disabled={anyPending}
                loading={updateMutation.isPending}
              >
                Save
              </Button>
            )}
            {isPublished && (
              <>
                <Button
                  variant="default"
                  onClick={() =>
                    createDraftMutation.mutate({
                      data: { publishedId: ctpId },
                    })
                  }
                  disabled={anyPending}
                  loading={createDraftMutation.isPending}
                >
                  Create Draft
                </Button>
                <Button
                  variant="destructive"
                  onClick={() =>
                    archiveMutation.mutate({ data: { id: ctpId } })
                  }
                  disabled={anyPending}
                  loading={archiveMutation.isPending}
                >
                  Archive
                </Button>
              </>
            )}
            {isInReview && (
              <>
                <Button
                  variant="primary"
                  onClick={() =>
                    approveMutation.mutate({ data: { id: ctpId } })
                  }
                  disabled={anyPending}
                  loading={approveMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  variant="default"
                  onClick={() => {
                    const note = window.prompt("Feedback note for the author:");
                    if (note) {
                      returnToDraftMutation.mutate({
                        data: { id: ctpId, note },
                      });
                    }
                  }}
                  disabled={anyPending}
                  loading={returnToDraftMutation.isPending}
                >
                  Return to Draft
                </Button>
              </>
            )}
            {isArchived && (
              <Button
                variant="default"
                onClick={() => restoreMutation.mutate({ data: { id: ctpId } })}
                disabled={anyPending}
                loading={restoreMutation.isPending}
              >
                Restore to Draft
              </Button>
            )}
          </div>
        </div>

        {/* Draft-of banner */}
        {hasDraftParent && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-700">
              Editing draft of published content. Save your changes, then
              promote when ready.
            </p>
            <div className="flex shrink-0 gap-2">
              <Button
                variant="destructive-ghost"
                size="sm"
                onClick={() => {
                  if (
                    window.confirm(
                      "Discard this draft? The live page stays as it is.",
                    )
                  ) {
                    discardDraftMutation.mutate({
                      data: { kind: "program", draftId: ctpId },
                    });
                  }
                }}
                disabled={anyPending}
                loading={discardDraftMutation.isPending}
              >
                Discard draft
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  promoteMutation.mutate({ data: { draftId: ctpId } })
                }
                disabled={anyPending}
                loading={promoteMutation.isPending}
                className="bg-amber-600! hover:bg-amber-700!"
              >
                Promote to Live
              </Button>
            </div>
          </div>
        )}

        {hasDraftParent && (
          <div className="mb-4">
            <DraftReviewCard kind="program" draftId={ctpId} />
          </div>
        )}

        {successMessage && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {mutationError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {mutationError instanceof Error
              ? mutationError.message
              : "An error occurred. Please try again."}
          </div>
        )}

        {/* Form sections */}
        <div className="space-y-6">
          {/* Basic Info */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Basic Info
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ctp-name" className="mb-1">
                    Name
                  </Label>
                  <Input
                    id="ctp-name"
                    type="text"
                    value={ctp.name}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>
                <div>
                  <Label htmlFor="ctp-slug" className="mb-1">
                    Slug
                  </Label>
                  <Input
                    id="ctp-slug"
                    type="text"
                    value={ctp.slug ?? ""}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <form.Field name="ccoId">
                  {(field) => (
                    <div>
                      <Label htmlFor="ctp-cco" className="mb-1">
                        Owning CCO{" "}
                        <span className="font-normal text-red-500">
                          (required)
                        </span>
                      </Label>
                      <Select
                        value={field.state.value || "__none__"}
                        onValueChange={(val) => {
                          const nextCcoId = val === "__none__" ? "" : val;
                          field.handleChange(nextCcoId);
                          // Drop the new owner from the accreditor set in the
                          // store, not merely from what the picker renders.
                          // Filtering only the projection leaves the id in the
                          // form value, so moving the owner on to a third
                          // organization would bring it back and the next save
                          // would silently reattach a link the admin had
                          // already removed and never reselected.
                          form.setFieldValue("accreditorIds", (current) =>
                            current.filter((id) => id !== nextCcoId),
                          );
                        }}
                      >
                        <SelectTrigger id="ctp-cco" className="w-full">
                          <SelectValue placeholder="Select a CCO..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">
                            — select a CCO —
                          </SelectItem>
                          {ccos?.map((cco) => (
                            <SelectItem key={cco.id} value={cco.id}>
                              {cco.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </form.Field>
                <form.Field name="website">
                  {(field) => (
                    <div>
                      <Label htmlFor="ctp-website" className="mb-1">
                        Website
                      </Label>
                      <Input
                        id="ctp-website"
                        type="url"
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        onBlur={field.handleBlur}
                        placeholder="https://..."
                      />
                    </div>
                  )}
                </form.Field>
              </div>
              <form.Field name="logoUrl">
                {(field) => (
                  <ImageUploadField
                    contentType="ctps"
                    contentId={ctpId}
                    purpose="logo"
                    currentUrl={field.state.value || null}
                    onUpload={field.handleChange}
                    onRemove={() => field.handleChange("")}
                    label="Logo"
                    recommendedDimensions="400x400"
                  />
                )}
              </form.Field>
            </div>
          </Card>

          {/* Program Details */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Program Details
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <form.Field name="deliveryFormat">
                  {(field) => (
                    <div>
                      <Label htmlFor="ctp-format" className="mb-1">
                        Delivery Format
                      </Label>
                      <Input
                        id="ctp-format"
                        type="text"
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        onBlur={field.handleBlur}
                        placeholder="e.g., Online (virtual, live synchronous)"
                      />
                    </div>
                  )}
                </form.Field>
                <form.Field name="accreditationLevel">
                  {(field) => (
                    <div>
                      <Label htmlFor="ctp-accreditation" className="mb-1">
                        Accreditation Level
                      </Label>
                      <Input
                        id="ctp-accreditation"
                        type="text"
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        onBlur={field.handleBlur}
                        placeholder="e.g., NBHWC Approved, ICF ACC-eligible"
                      />
                    </div>
                  )}
                </form.Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <form.Field name="duration">
                  {(field) => (
                    <div>
                      <Label htmlFor="ctp-duration" className="mb-1">
                        Duration
                      </Label>
                      <Input
                        id="ctp-duration"
                        type="text"
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        onBlur={field.handleBlur}
                        placeholder="e.g., 6-18 months"
                      />
                    </div>
                  )}
                </form.Field>
                <form.Field name="tuitionRange">
                  {(field) => (
                    <div>
                      <Label htmlFor="ctp-tuition" className="mb-1">
                        Tuition Range
                      </Label>
                      <Input
                        id="ctp-tuition"
                        type="text"
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        onBlur={field.handleBlur}
                        placeholder="e.g., $1,985 - $3,640"
                      />
                    </div>
                  )}
                </form.Field>
                <form.Field name="icfHours">
                  {(field) => (
                    <div>
                      <Label htmlFor="ctp-icf" className="mb-1">
                        ICF Hours
                      </Label>
                      <Input
                        id="ctp-icf"
                        type="text"
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        onBlur={field.handleBlur}
                        placeholder="e.g., 60"
                      />
                    </div>
                  )}
                </form.Field>
              </div>
              <form.Field name="prerequisites">
                {(field) => (
                  <div>
                    <Label htmlFor="ctp-prereqs" className="mb-1">
                      Prerequisites
                    </Label>
                    <Textarea
                      id="ctp-prereqs"
                      rows={3}
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      placeholder="Prerequisites for this program..."
                    />
                  </div>
                )}
              </form.Field>
            </div>
          </Card>

          {/* Frameworks Taught */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Frameworks Taught
            </h2>
            <p className="mb-4 text-sm text-gray-400">
              Link coaching frameworks and methodologies taught by this training
              program.
            </p>
            <FrameworkSelection
              selectedIds={selectedFrameworkIds}
              onSelectionChange={setSelectedFrameworkIds}
              groupedFrameworks={groupedFrameworks}
            />
          </Card>

          {/* Accredited By */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Accredited By
            </h2>
            <p className="mb-4 text-sm text-gray-400">
              Organizations that accredit this program — not the school that
              delivers it, which is the Organization field above. This is what
              makes the program appear on an accreditation body&apos;s page.
            </p>
            <form.Field name="accreditorIds">
              {(field) => (
                <GroupedMultiSelect
                  selectedIds={accreditorIds}
                  onSelectionChange={field.handleChange}
                  groupedItems={groupedAccreditors}
                  groupHeading={(type) => CCO_TYPE_DISPLAY_NAME[type] ?? type}
                  itemNoun="organization"
                  placeholder="Search organizations..."
                />
              )}
            </form.Field>
          </Card>

          {/* Narrative (dark band) */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Narrative & Stats
            </h2>
            <form.Field name="pullQuote">
              {(field) => (
                <div>
                  <Label htmlFor="ctp-pull-quote" className="mb-1">
                    Pull quote (rendered in the dark § 02 band)
                  </Label>
                  <Textarea
                    id="ctp-pull-quote"
                    rows={3}
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="One sentence that captures the training program's mission"
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="pullQuoteAttribution">
              {(field) => (
                <div className="mt-4">
                  <Label htmlFor="ctp-pull-quote-attr" className="mb-1">
                    Pull quote attribution
                  </Label>
                  <Input
                    id="ctp-pull-quote-attr"
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="e.g., Program Founder"
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="narrativeStats" mode="array">
              {(field) => (
                <div className="mt-4">
                  <Label className="mb-1">Narrative stats (up to 4)</Label>
                  <p className="mb-2 text-xs text-gray-500">
                    Big numbers about the training program (e.g., "60" / "ICF
                    hours"). Render in the hero stat strip before
                    platform-derived stats.
                  </p>
                  {(field.state.value ?? []).map((_, i) => (
                    <div key={i} className="mb-2 flex gap-2">
                      <form.Field name={`narrativeStats[${i}].value`}>
                        {(f) => (
                          <Input
                            placeholder="60"
                            value={f.state.value ?? ""}
                            onValueChange={f.handleChange}
                            className="w-32"
                          />
                        )}
                      </form.Field>
                      <form.Field name={`narrativeStats[${i}].label`}>
                        {(f) => (
                          <Input
                            placeholder="ICF hours"
                            value={f.state.value ?? ""}
                            onValueChange={f.handleChange}
                            className="flex-1"
                          />
                        )}
                      </form.Field>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => field.removeValue(i)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  {(field.state.value?.length ?? 0) < 4 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => field.pushValue({ value: "", label: "" })}
                    >
                      + Add stat
                    </Button>
                  ) : null}
                </div>
              )}
            </form.Field>

            <form.Field name="hidePlatformStats">
              {(field) => (
                <div className="mt-4 flex items-center gap-2">
                  <input
                    id="ctp-hide-platform-stats"
                    type="checkbox"
                    checked={!!field.state.value}
                    onChange={(e) => field.handleChange(e.target.checked)}
                  />
                  <Label htmlFor="ctp-hide-platform-stats">
                    Hide platform-derived stats (only show narrative stats
                    above)
                  </Label>
                </div>
              )}
            </form.Field>
          </Card>

          {/* Description */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Description
            </h2>
            <form.Field name="description">
              {(field) => (
                <div>
                  <Label htmlFor="ctp-description" className="mb-1">
                    Description{" "}
                    <span className="font-normal text-gray-400">
                      (meta / SEO)
                    </span>
                  </Label>
                  <Textarea
                    id="ctp-description"
                    rows={4}
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                    placeholder="Brief description of this training program..."
                  />
                </div>
              )}
            </form.Field>
          </Card>

          {/* Body */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              Body
            </h2>
            <form.Field name="body">
              {(field) => (
                <RichTextEditor
                  content={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
          </Card>

          {/* FAQs */}
          <Card>
            <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
              FAQs
            </h2>
            <form.Field name="faqs">
              {(field) => (
                <FaqEditor
                  value={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
          </Card>

          {/* Certified coaches — reverse-lookup */}
          <ReverseLookupCard
            title="Certified Coaches"
            summary={`${coachCount} coach${coachCount === 1 ? "" : "es"} hold a certification in this training program.`}
            pointer="To add, remove, or verify coach certifications, open the Verifications page."
          >
            <Link
              to="/admin/verifications"
              search={{ tab: "ctp", ctpId }}
              className="text-sm text-brand-600 hover:underline"
            >
              View on Verifications page
            </Link>
          </ReverseLookupCard>

          <Card>
            <h2 className="text-sm font-medium tracking-wide text-gray-500 uppercase">
              Change history
            </h2>
            <PageChangeHistory ctpId={ctp.draftOf ?? ctpId} />
          </Card>
        </div>

        {/* Sticky save for draft */}
        {isDraft && (
          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={anyPending}
              loading={updateMutation.isPending}
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
